import { queryOptions } from "@tanstack/react-query";

export interface Link {
  href: string;
  rel: string;
  type?: string;
  title?: string;
  hreflang?: string;
  length?: number;
  profile?: Array<string>;
}

export interface CommonProperties {
  created?: string;
  updated?: string;
  title?: string;
  description?: string;
  type?: string;
  keywords?: Array<string>;
  themes?: Array<{
    scheme: string;
    concepts: Array<{
      id: string;
      title?: string;
      description?: string;
      url?: string;
    }>;
  }>;
  language?: object;
  languages?: Array<object>;
  resourceLanguages?: Array<object>;
  externalIds?: Array<{ scheme?: string; value: string }>;
  formats?: Array<{ name?: string; mediaType?: string }>;
  contacts?: Array<object>;
  license?: string;
  rights?: string;
}

export interface Collection extends CommonProperties {
  id: string;
  title: string;
  description?: string;
  extent?: object;
  crs?: Array<string>;
  conformsTo?: Array<string>;
  type: "Collection";
  itemType?: "record" | "catalog" | Array<"record" | "catalog">;
  links: Array<Link>;
}

export interface Record {
  id: string;
  collection: string;
  time?: string;
  conformsTo?: Array<string>;
  geometry?: object;
  properties: CommonProperties & {
    constraints?: Array<{
      type: "use" | "access" | "other";
      title: string;
      url: string;
    }>;
  };
  links: Array<Link>;
}

export interface RecordsResponse {
  type: "FeatureCollection";
  features: Array<Record>;
}

function makeFetch<T>(path: string | URL, init?: RequestInit) {
  return async () => {
    const response = await fetch(path, init);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${path}`);
    }
    return (await response.json()) as Promise<T>;
  };
}

export interface CatalogFilters {
  type?: Array<string>;
  q?: string;
  limit?: number;
}

export interface CatalogOptions {
  language?: string;
  token?: string;
}

export const DATA_URL = import.meta.env.VITE_DATA_URL || window.location.origin;

export const catalog = {
  collections: queryOptions({
    queryKey: ["catalog", "collections"],
    queryFn: makeFetch<Collection>(`${DATA_URL}/catalog/collections`),
  }),
  items: (
    collectionId: string,
    filters?: CatalogFilters,
    options?: CatalogOptions,
  ) => {
    const url = new URL(
      `${DATA_URL}/catalog/collections/${collectionId}/items`,
    );
    if (filters) {
      const search = new URLSearchParams();
      if (filters.q) {
        search.set("q", filters.q);
      }
      if (filters.type) {
        for (const type of filters.type) {
          search.append("type", type);
        }
      }
      if (filters.limit) {
        search.set("limit", String(filters.limit));
      }
      url.search = search.toString();
    }

    return queryOptions({
      queryKey: [
        "catalog",
        "collections",
        collectionId,
        "items",
        JSON.stringify(filters ?? {}),
        options?.language ?? "",
      ],
      queryFn: makeFetch<RecordsResponse>(url, {
        headers: {
          ...(options?.language ? { "Accept-Language": options.language } : {}),
          ...(options?.token
            ? { Authorization: `Bearer ${options.token}` }
            : {}),
        },
      }),
      enabled: !!options?.token,
    });
  },
};
